import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import flatMap from 'lodash-es/flatMap';
import forEach from 'lodash-es/forEach';
import isArray from 'lodash-es/isArray';
import get from 'lodash-es/get';
import kebabCase from 'lodash-es/kebabCase';
import isEmpty from 'lodash-es/isEmpty';
import cloneDeep from 'lodash-es/cloneDeep';

import { SCHEDULE_TYPES, MEAL_PERIOD_CONSTANTS } from '@finder/shared/constants/app.constants';
import { ModalComponent } from '@finder/shared/components/modal/modal.component';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { ShowtimesModalComponent } from '@finder/shared/components/showtimes-modal/showtimes-modal.component';
import { MealPeriodScheduleService } from '@finder/shared/services/meal-period-schedule/meal-period-schedule.service';
import { ScheduleService } from '@finder/shared/services/schedule/schedule.service';
import { MEAL_PERIOD_LAYOUT } from '@finder/shared/components/meal-period/meal-period.constants';
import { CommonModule } from '@angular/common';
import { MealPeriodComponent } from '../meal-period/meal-period.component';
import { ScheduleListComponent } from '../schedule-list/schedule-list.component';
import { AnchorComponent } from '../anchor/anchor.component';
import { ButtonComponent } from '../button/button.component';

@Component({
    selector: 'finder-schedule',
    templateUrl: './schedule.component.html',
    styleUrls: ['./schedule.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MealPeriodComponent,
        ScheduleListComponent,
        AnchorComponent,
        ButtonComponent
    ],
})
export class ScheduleComponent implements OnInit, OnDestroy {

    @Input() scheduleDate: Date;
    @Input() maxDays;
    @Input() dashStatus;

    modal: ModalComponent;
    scheduleIsEmptyMessage: string;
    emptySchedules = false;
    emptyPerformance = false;
    showViewMore = false;
    closedSchedule = false;
    showtimesModalLinkTitle: string;
    showtimesModalLinkName: string;
    showtimesModalTitle: string;
    scheduleToLabel: string;
    scheduleAndLabel: string;
    maxSchedulesToShow = 3;
    scheduleLabels = {};
    scheduleGroups = {};
    scheduleTypeLinks = {};
    scheduleTypeClassName = {};
    totalSchedules = 0;
    scheduleKeys = [];
    types: any;
    mealPeriodSchedules;
    dinnerShowRestaurant;
    mealPeriodsOrder;
    layouts = MEAL_PERIOD_LAYOUT;

    private destroy$ = new Subject<void>();
    private _schedule: any;

    @Input()
    set schedule(schedule: any) {
        this._schedule = schedule;
        this.setupSchedules();
    }

    get schedule(): any {
        return this._schedule;
    }

    constructor(
        public modalService: ModalService,
        public scheduleService: ScheduleService,
        public mealPeriodScheduleService: MealPeriodScheduleService,
    ) { }

    ngOnInit() {
        this.setup();
        this.processDiningData();
        this.setScheduleTypeLinks();
        this.setCssClassToScheduleTypes();

        // Only subscribe if we're on a dining page
        if (this.mealPeriodSchedules) {
            this.mealPeriodScheduleService.scheduleUpdated
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.processDiningData();
                });
        }

    }

    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    setupSchedules() {
        this.totalSchedules = 0;
        this.types = SCHEDULE_TYPES;
        this.scheduleKeys = Object.keys(this.schedule.schedules);
        this.emptyPerformance = this.scheduleKeys.includes(this.types.noPerformance);
        this.closedSchedule = this.scheduleKeys.includes(this.types.closedSchedule) &&
            this.scheduleKeys.length === 1;
        this.emptySchedules = this.scheduleKeys.length === 0 || this.emptyPerformance || this.closedSchedule || this.dashStatus;

        if (!this.emptySchedules) {
            forEach(this.schedule.schedules, (value, type) => {
                this.scheduleGroups[type] = isArray(value) ? value : flatMap(value);
                // if Extra magic hours or Operating or Extra Magic Morning has only 2 schedules
                // subtract one to the total count since will be show in 1 line.
                this.totalSchedules += this.scheduleGroups[type].length === 2 ? 1 : this.scheduleGroups[type].length;
                this.scheduleGroups[type] = this.removeExtraSchedulesToShow(this.scheduleGroups[type]);
            });
        }
        this.showViewMore = this.totalSchedules > this.maxSchedulesToShow;
    }

    setupLabels() {
        if (this.schedule.labels) {
            // For better reading.
            const labels = this.schedule.labels;
            this.scheduleLabels = this.scheduleService.getScheduleLabels(labels);
            this.scheduleLabels[this.types.performanceTime] =
                this.scheduleGroups[this.types.performanceTime] &&
                this.scheduleGroups[this.types.performanceTime].length > 1 ?
                    labels.pluralLabel :
                    labels.singularLabel;

            this.scheduleToLabel = labels.scheduleToLabel;
            this.scheduleAndLabel = labels.scheduleAndLabel;
            this.showtimesModalLinkName = labels.modalLinkName;
            this.showtimesModalLinkTitle = labels.modalLinkTitle;
            this.showtimesModalTitle = labels.showtimesModalTitle;
            this.scheduleIsEmptyMessage = this.emptyPerformance ? labels.noPerformance : labels.emptySchedule;
        }
    }

    createShowtimesModal() {
        this.modal = this.modalService.create(
            ShowtimesModalComponent,
            {
                title: this.showtimesModalTitle,
                hideOnClose: true,
                id: 'showtimes-modal',
                data: {
                    maxDays: this.maxDays
                }
            }
        );
    }

    openShowtimesModal() {
        this.modal.open();
    }

    removeExtraSchedulesToShow(schedules: Array<any>) {
        if (schedules && schedules.length > this.maxSchedulesToShow) {
            return schedules.slice(0, this.maxSchedulesToShow);
        }

        return schedules;
    }

    setScheduleTypeLinks() {
        if (this.schedule.scheduleTypeLinks) {
            this.schedule.scheduleTypeLinks.forEach(scheduleTypeObject => {
                this.scheduleTypeLinks[scheduleTypeObject.type] = scheduleTypeObject.url;
            });
        }
    }

    setCssClassToScheduleTypes() {
        this.scheduleKeys.forEach(scheduleType => {
            this.scheduleTypeClassName[scheduleType] = kebabCase(scheduleType);
        });
    }

    private setup() {
        // Create a new copy of the array so we can make changes
        this.mealPeriodsOrder = cloneDeep(MEAL_PERIOD_CONSTANTS.mealPeriodsOrder);

        // Add late night dining to the display
        this.mealPeriodsOrder.push(MEAL_PERIOD_CONSTANTS.lateNightDining);

        this.setupSchedules();
        this.setupLabels();
        this.createShowtimesModal();
    }

    /**
     * This function checks the meal period service to see if there's any meal period information
     * we need to display. If it's not a dine page it won't return any the variables will be empty.
     * We need to run this at least once to see if we're on a dining page.
     */
    private processDiningData() {
        this.dinnerShowRestaurant = get(this.mealPeriodScheduleService, 'mealPeriodsData.dinnerShowRestaurant');
        this.mealPeriodSchedules = get(this.mealPeriodScheduleService, 'mealPeriodsData.schedules') ||
            get(this.dinnerShowRestaurant, 'productSchedules');
        this.mealPeriodSchedules = isEmpty(this.mealPeriodSchedules) ? false : this.mealPeriodSchedules;
    }
}
